/**
 * Pure dispute clustering for the compiled-workflow product surface
 * (docs/optimization/COMPILED-WORKFLOW-SURFACE-PRD.md §5).
 *
 * Inverse-direction pairs are the SAME human question ("feature vs improvement"
 * ≡ "improvement vs feature") — the key sorts the two candidate values so both
 * directions collapse. Real-origin disputes are never batch-grouped (caller
 * filters; this helper assumes the list it receives is already the synthetic
 * batchable set, or passes through origin if present).
 *
 * No React, no DB — unit tests and the product client share this module.
 */

export interface DisputeLike {
  exampleKey: string;
  axis: string;
  blast: "low" | "high";
  valueGenerated: string;
  valueLlm: string;
  title?: string;
  description?: string;
  difficulty?: string | null;
  origin?: "synthetic" | "real";
}

export interface DisputeCluster<T extends DisputeLike = DisputeLike> {
  /** Unordered pair key: `${axis}|${a}|${b}` with a,b locale-sorted. */
  key: string;
  axis: string;
  blast: "low" | "high";
  /** Sorted candidate A (display/keyboard "1"). */
  a: string;
  /** Sorted candidate B (display/keyboard "2"). */
  b: string;
  members: T[];
}

/** Stable unordered key for one (axis, candidate-pair) decision. */
export function clusterKey(axis: string, valueA: string, valueB: string): string {
  const [a, b] = [valueA, valueB].sort((x, y) => x.localeCompare(y));
  return `${axis}|${a}|${b}`;
}

/**
 * Group disputes into unordered-pair clusters. High-blast first, then largest
 * clusters (most examples settled per decision). When `syntheticOnly` is true
 * (default), `origin === "real"` rows are excluded — they must pin individually.
 */
export function buildDisputeClusters<T extends DisputeLike>(
  disputes: readonly T[],
  opts: { syntheticOnly?: boolean } = {},
): DisputeCluster<T>[] {
  const syntheticOnly = opts.syntheticOnly !== false;
  const map = new Map<string, DisputeCluster<T>>();
  for (const d of disputes) {
    if (syntheticOnly && d.origin === "real") continue;
    const key = clusterKey(d.axis, d.valueGenerated, d.valueLlm);
    let c = map.get(key);
    if (!c) {
      const [a, b] = [d.valueGenerated, d.valueLlm].sort((x, y) => x.localeCompare(y));
      c = {
        key,
        axis: d.axis,
        blast: d.blast,
        a: a!,
        b: b!,
        members: [],
      };
      map.set(key, c);
    }
    c.members.push(d);
    if (d.blast === "high") c.blast = "high";
  }
  return [...map.values()].sort(
    (x, y) =>
      Number(y.blast === "high") - Number(x.blast === "high") ||
      y.members.length - x.members.length,
  );
}
